import React, {
  useEffect,
} from 'react';
import { useIsHomeRoute } from '@/hooks/useIsHomeRoute';
import GatorProtectAcknowledgedModal from './GatorProtectAcknowledgedModal';
import GatorProtectDetectedThreadsModal from './GatorProtectDetectedThreadsModal';
import type { GatorProtectAcknowledgedModalFields } from './GatorProtectAcknowledgedModal';
import type {
  GatorProtectGlobalsProps,
  GatorProtectGlobalsRoutes,
} from './GatorProtectGlobals.types';
import { useGatorProtectGlobal } from './useGatorProtectGlobal';

/**
 * Global Gator Protect side effects and modals in the header.
 *
 * Visibility:
 * - `globals.forceAcknowledgeModalVisible` / `globals.forceDetectedThreadsModalVisible`, when defined,
 *  Data and requests live in `useGatorProtectGlobal`: on the **home** route it may dispatch
 * `portalModalsActions.fetch` (`GET /api/v3/modals` via saga) when `gator_protect` is enabled; leaving home
 *  or disabling the feature clears `portalModals` in Redux. Plans list is requested at most once per page load. Modal field props are composed here (not passed through `globals`).
 */
export function GatorProtectGlobals({ globals: globalsProp, dependencies }: GatorProtectGlobalsProps) {
  const { summary } = dependencies;
  const { isHome, history, routes: routesLocale } = useIsHomeRoute<GatorProtectGlobalsRoutes>();

  const {
    utils: { fetchModalsWhenHome, getGatorProtectPlans, hideDetectedThreadsModal },
    data: { detectedThreads },
    gatorProtectModals: { showAcknowledgment, showDetectedThreads },
  } = useGatorProtectGlobal(summary, isHome);

  const showDetectedThreadsModal = globalsProp?.forceDetectedThreadsModalVisible ?? showDetectedThreads;
  const showAcknowledgeModal = globalsProp?.forceAcknowledgeModalVisible ?? showAcknowledgment;

  // TODO: Implement acknowledgment modal logic (placeholder for now)
  const acknowledgedModalProps: GatorProtectAcknowledgedModalFields = {
    handleClose: () => {},
    handleRedirect: () => {},
    selectedPlan: {
      expire_date: '2030-06-01',
      expire_days: 30,
      is_trial: false,
      shared_hosting_plan: 'Turbo',
    },
  };

  useEffect(() => {
    fetchModalsWhenHome();
  }, [fetchModalsWhenHome]);


  useEffect(() => {
    getGatorProtectPlans();
  }, [getGatorProtectPlans]);

  const onDetectedThreadsCTAHandler = () => {
    if (detectedThreads == null) {
      return;
    }
    history.push(`${routesLocale.gatorProtect}/${detectedThreads.hostingId}`);
  };

  return (
    <>
      {showAcknowledgeModal && (
        <GatorProtectAcknowledgedModal {...acknowledgedModalProps} />
      )}
      {showDetectedThreadsModal && (
        <GatorProtectDetectedThreadsModal
          threatCount={detectedThreads?.threatCount ?? 0}
          onClose={hideDetectedThreadsModal}
          onPrimary={onDetectedThreadsCTAHandler}
        />
      )}
    </>
  );
}
